import { ComponentProps, HTMLAttributes, PropsWithChildren } from "react";
import Card from "@particles/card/Card";
import { ConfigurationSettings } from "@particles/configurationSettings/utils/types";
import { FeedTag } from "@routes/Keyword/utils/types";
import { FeedSection } from "@routes/Section/utils/types";
import { TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { SanitizedSectionArticle } from "@typings/Cue";
import { DefaultNavigationData } from "@typings/Page";

export type MyAccountPageContext = TRouteWithRedirect<
  MyAccountPagePayload,
  string
>;

export type MyAccountPagePayload = {
  data: MyAccountPageData;
} & ConfigurationSettings;

export type MyAccountPageData = DefaultNavigationData;

export enum MyAccountTabsEnum {
  "myBT" = "myBT",
  "recent" = "Recently Read",
}

export type useGetMyBTArticleListProps = {
  /** List of tags to fetch */
  tags: FeedTag[];
  /** Size of tags to fetch */
  tagSize?: number;
  /** Size of articles to fetch */
  size?: number;
  /** Indicator to fetch tags for popover */
  isPopover?: boolean;
};

export type myBTTagsWithArticle = {
  tag: FeedTag;
  articles: ComponentProps<typeof Card>[];
};

export type MyAccountActiveProps = {
  activeTab: MyAccountTabsEnum;
};

export type MyAccountTabsProps = {
  activeTab: MyAccountTabsEnum;
  handleTabChange: React.Dispatch<React.SetStateAction<MyAccountTabsEnum>>;
} & Pick<HTMLAttributes<HTMLDivElement>, "className">;

export type MyAccountPopoverMyBTProps = {
  firstCharOfLogin?: string;
};

export type MyAccountMyBTAccordionProps = {
  title: string;
} & PropsWithChildren &
  Pick<HTMLAttributes<HTMLDivElement>, "className">;

export type MyAccountMyBTPageListProps = {
  tagWithArticleList: myBTTagsWithArticle[];
  loadMoreRef?: (node?: Element | null | undefined) => void;
};

export type MyAccounMyBTPopoverListProps = {
  tagWithArticleList: myBTTagsWithArticle[];
};

export type MyAccountMyBTBasicCardProps = {
  article: ComponentProps<typeof Card>;
};

export type MyAccountMyBTTagFilterProps = {
  tagList: string[];
  activeFilter: string;
  setActiveFilter: (tagName: string) => void;
};

export type MyAccountReadingHistoryBasicCardPopoverProps = {
  article: SanitizedSectionArticle;
};

export type useGetRecentArticleListProps = {
  /** Page size of articles to display per scroll */
  pageSize: number;
};

export type MyAccountReadingHistoryPopoverProps = {
  /** The list of article to dispay */
  articleList: ComponentProps<typeof Card>[];
  /** Indicator to display view more link */
  isDisplayViewMore?: boolean;
};

export type MyAccountReadingHistoryProps = {
  /** The list of article to dispay */
  articleList: ComponentProps<typeof Card>[];
  /** Compenent ref to load more */
  loadMoreRef?: (node?: Element | null | undefined) => void;
  /** Callback to handle delete article */
  handleDeleteArticle: (articleId: string) => void;
  /** List of article Id that is being deleted */
  deleteArticleIdList: string[];
};

export type FeedReadingHistoryStore = {
  /** List of recent read article Ids */
  articleList: ComponentProps<typeof Card>[];
  /** Function to set the article Id list */
  setArticleId: (articleIdList: ComponentProps<typeof Card>) => void;
  /** Function to delete article Id from the list */
  deleteArticleId: (articleId: ComponentProps<typeof Card>) => void;
};

export type FeedReadingHistoryData = {
  /** The recent read article id */
  articleId: string;
  /** The date which use read */
  readAt: string;
  /** The Article url of the recent read */
  articleUrl: string;
  /** The published time stamp of the article*/
  publishedTime: string;
  /** The edited time stamp of the articles */
  editedTime: string;
  /** The section details */
  sections?: FeedSection[];
  /** The title of the article */
  title: string;
  /** The platform from which the article was read */
  platform: string;
};

export type FeedSetReadingHistoryResponse = {
  /** Timestamp which user read */
  timeStamp: string;
};

export type FeedDeleteReadingHistoryResponse = {
  /** Successful message from BE */
  message: string;
};
